import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode
} from 'react';
import type { AuthenticatedUser, UserRole } from '@services/auth';
import { hasAdminCapability, useAuth } from '@services/auth';

export type AdminViewAsTarget = {
	id: number;
	name: string;
	email: string;
	role: UserRole;
};

const STORAGE_KEY = 'gardenhub:adminViewAs';

type AdminViewAsContextValue = {
	viewAsTarget: AdminViewAsTarget | null;
	setViewAsTarget: (t: AdminViewAsTarget | null) => void;
	clearViewAs: () => void;
};

const AdminViewAsContext = createContext<AdminViewAsContextValue | undefined>(undefined);

function parseStoredTarget(raw: string | null): AdminViewAsTarget | null {
	if (!raw) return null;
	try {
		const o = JSON.parse(raw) as Partial<AdminViewAsTarget>;
		if (
			o &&
			typeof o.id === 'number' &&
			o.id > 0 &&
			typeof o.role === 'string' &&
			['seeker', 'gardener', 'company', 'admin'].includes(o.role)
		) {
			return {
				id: o.id,
				name: typeof o.name === 'string' ? o.name : '',
				email: typeof o.email === 'string' ? o.email : '',
				role: o.role as UserRole
			};
		}
	} catch {
		/* ignore */
	}
	return null;
}

export function AdminViewAsProvider({ children }: { children: ReactNode }) {
	const { user } = useAuth();
	const [viewAsTarget, setViewAsTargetState] = useState<AdminViewAsTarget | null>(null);

	useEffect(() => {
		if (!user) {
			setViewAsTargetState(null);
			try {
				sessionStorage.removeItem(STORAGE_KEY);
			} catch {
				/* ignore */
			}
			return;
		}
		if (user.role !== 'admin' || !hasAdminCapability(user, 'admin_view_as')) {
			setViewAsTargetState(null);
			return;
		}
		try {
			setViewAsTargetState(parseStoredTarget(sessionStorage.getItem(STORAGE_KEY)));
		} catch {
			setViewAsTargetState(null);
		}
	}, [user]);

	const setViewAsTarget = useCallback(
		(t: AdminViewAsTarget | null) => {
			if (!user || user.role !== 'admin' || !hasAdminCapability(user, 'admin_view_as')) {
				return;
			}
			setViewAsTargetState(t);
			try {
				if (t) {
					sessionStorage.setItem(STORAGE_KEY, JSON.stringify(t));
				} else {
					sessionStorage.removeItem(STORAGE_KEY);
				}
			} catch {
				/* ignore */
			}
		},
		[user]
	);

	const clearViewAs = useCallback(() => setViewAsTarget(null), [setViewAsTarget]);

	const value = useMemo(
		() => ({ viewAsTarget, setViewAsTarget, clearViewAs }),
		[viewAsTarget, setViewAsTarget, clearViewAs]
	);

	return <AdminViewAsContext.Provider value={value}>{children}</AdminViewAsContext.Provider>;
}

export function useAdminViewAs(): AdminViewAsContextValue {
	const ctx = useContext(AdminViewAsContext);
	if (!ctx) {
		throw new Error('useAdminViewAs must be used within AdminViewAsProvider');
	}
	return ctx;
}

/** Seeker id for My Plans / seeker projects / seeker offers when admin is viewing as a seeker */
export function getAdminEffectiveSeekerIdNum(
	user: AuthenticatedUser | null,
	viewAs: AdminViewAsTarget | null
): number | null {
	if (!user) return null;
	if (user.role === 'seeker') {
		const n = parseInt(String(user.id), 10);
		return Number.isFinite(n) ? n : null;
	}
	if (user.role === 'admin' && viewAs?.role === 'seeker') {
		return viewAs.id;
	}
	return null;
}

/**
 * `plans.user_id` for the "My Plans" page: the account that owns rows in `plans`, including drafts.
 * When an admin uses "View as…", use that user's id for **any** role (e.g. company accounts still own seeker-flow plans in DB).
 */
export function getAdminEffectiveMyPlansOwnerUserIdNum(
	user: AuthenticatedUser | null,
	viewAs: AdminViewAsTarget | null
): number | null {
	if (!user) return null;
	if (user.role === 'seeker') {
		const n = parseInt(String(user.id), 10);
		return Number.isFinite(n) ? n : null;
	}
	if (user.role === 'admin' && viewAs) {
		return viewAs.id;
	}
	return null;
}

/** Gardener or company user id for projects/tasks/sent offers when admin is viewing as that user */
export function getAdminEffectiveCompanyOrGardenerIdNum(
	user: AuthenticatedUser | null,
	viewAs: AdminViewAsTarget | null
): number | null {
	if (!user) return null;
	if (user.role === 'gardener' || user.role === 'company') {
		const n = parseInt(String(user.id), 10);
		return Number.isFinite(n) ? n : null;
	}
	if (user.role === 'admin' && viewAs && (viewAs.role === 'gardener' || viewAs.role === 'company')) {
		return viewAs.id;
	}
	return null;
}

/**
 * Identity passed to calendar API. When admin has no target, keep admin (global calendar).
 * When admin views as someone, use that user's id and role so the calendar matches theirs.
 */
export function getAdminCalendarIdentity(
	user: AuthenticatedUser,
	viewAs: AdminViewAsTarget | null
): { userId: string; role: UserRole } {
	if (user.role === 'admin' && viewAs) {
		return { userId: String(viewAs.id), role: viewAs.role };
	}
	return { userId: user.id, role: user.role };
}

export function isAdminViewingAsOther(
	user: AuthenticatedUser | null,
	viewAs: AdminViewAsTarget | null
): boolean {
	return Boolean(user?.role === 'admin' && viewAs && viewAs.id !== parseInt(String(user.id), 10));
}

/**
 * User id to send as `user_id` / `seeker_id` / `gardener_id` on messaging and offer-access APIs.
 * When an admin uses “View as…”, APIs expect the impersonated user’s id, not the admin’s.
 */
export function getMessagingActingUserId(
	user: AuthenticatedUser | null,
	viewAs: AdminViewAsTarget | null
): number | null {
	if (!user) return null;
	if (user.role === 'admin' && viewAs) {
		return viewAs.id;
	}
	const n = parseInt(String(user.id), 10);
	return Number.isFinite(n) ? n : null;
}

/**
 * Seeker-side APIs (plan owner, offer seeker_id, project conversation as seeker).
 * Prefer admin view-as seeker; then real seeker; then optional plan owner (admin browsing a seeker URL without view-as).
 */
export function getSeekerPlanActorUserId(
	user: AuthenticatedUser | null,
	viewAs: AdminViewAsTarget | null,
	opts?: { planOwnerUserId?: number | null }
): number | null {
	const lens = getAdminEffectiveSeekerIdNum(user, viewAs);
	if (lens != null) return lens;
	if (user?.role === 'seeker') {
		const n = parseInt(String(user.id), 10);
		return Number.isFinite(n) ? n : null;
	}
	if (user?.role === 'admin' && opts?.planOwnerUserId != null) {
		const n = Number(opts.planOwnerUserId);
		return Number.isFinite(n) && n > 0 ? n : null;
	}
	return null;
}
